// ============================================================
// Viashift — Scan helpers (Phase 2, Epic 2B.2 + 2B.3)
// ============================================================
// Centralizes the data-side of the scan flow:
//
//   - lookupProductByBarcode: resolves a decoded value into
//     one of three outcomes (match / unknown / multi_match)
//   - recordScanDecrement: writes a `scan_decrement` event
//   - assignBarcodeToProduct: invokes set_product_barcode RPC
//   - recordScanUndo: writes a compensating manual_adjustment
//
// All clinic-scoped operations take an explicit clinicId and
// pass it through both as a query filter (defense-in-depth)
// and to RLS / RPC boundaries (the actual security gate).
//
// Note on event kinds:
//   - 'scan_decrement' — what we write on a successful scan.
//     The build plan ticket text uses 'scan_empty_bin' but the
//     shipped CHECK constraint pins 'scan_decrement'. Schema
//     wins.
//   - 'manual_adjustment' for the undo, with a notes field
//     documenting the cause. The audit trail stays honest:
//     we don't pretend the original event didn't happen, we
//     log a compensating delta.
// ============================================================

import { supabase } from './supabase';
import type { ProductRow } from './inventory';

export type LookupResult =
  | { kind: 'match'; product: ProductRow }
  | { kind: 'unknown' }
  | { kind: 'multi_match'; products: ProductRow[] };

/**
 * Look up a product by scanned barcode within a clinic.
 *
 * Multi-match is rare in practice (no uniqueness constraint
 * on `barcode` so technically possible, but the assignment
 * RPC explicitly rejects collisions on write) — surfaced as
 * its own outcome so the UI can disambiguate rather than
 * arbitrarily picking one.
 */
export async function lookupProductByBarcode(
  clinicId: string,
  barcode: string,
): Promise<LookupResult> {
  const trimmed = barcode.trim();
  if (trimmed === '') return { kind: 'unknown' };

  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('clinic_id', clinicId)
    .eq('barcode', trimmed);

  if (error) throw error;
  const rows = data ?? [];
  if (rows.length === 0) return { kind: 'unknown' };
  if (rows.length === 1) {
    // Length check above guarantees the index exists; the !
    // is for noUncheckedIndexedAccess.
    return { kind: 'match', product: rows[0]! };
  }
  return { kind: 'multi_match', products: rows };
}

/**
 * Record a scan-decrement event. The BEFORE INSERT trigger
 * on inventory_events atomically drives products.count down
 * and stamps count_after — same path as the manual stepper
 * in 2A.3, just a different `kind`.
 *
 * count_after: 0 placeholder — the trigger overwrites. See
 * the 2A.3 commit / DECISIONS for the rationale.
 *
 * The trigger refuses to drive products.count negative; we
 * surface a structured `negative_count` outcome rather than
 * propagating the raw exception — the UI distinguishes
 * "you scanned an empty bin" from "the database is broken."
 *
 * Two recognized error signatures (defense in depth — see
 * `isNegativeCountError`):
 *   1. The trigger's own raise message (post-migration 36,
 *      2026-05-26 — happy path).
 *   2. The `products_count_check` CHECK constraint name (only
 *      reachable if the trigger is bypassed, replaced, or a
 *      future code path inserts directly into products without
 *      going through inventory_events).
 */
export type ScanDecrementResult =
  | { kind: 'ok' }
  | { kind: 'negative_count' }
  | { kind: 'error'; message: string };

/**
 * True if a Postgres error message indicates the operation
 * would drive products.count below zero. Matches both the
 * trigger's friendly raise and the underlying CHECK
 * constraint name, so we surface gracefully even if the
 * trigger ever stops being the gate.
 */
function isNegativeCountError(message: string): boolean {
  return (
    message.includes('would drive products.count negative') ||
    message.includes('products_count_check')
  );
}

export async function recordScanDecrement(
  clinicId: string,
  productId: string,
  userId: string,
): Promise<ScanDecrementResult> {
  const { error } = await supabase.from('inventory_events').insert({
    clinic_id: clinicId,
    product_id: productId,
    kind: 'scan_decrement',
    delta: -1,
    count_after: 0,
    actor_id: userId,
  });

  if (!error) return { kind: 'ok' };

  if (isNegativeCountError(error.message)) {
    return { kind: 'negative_count' };
  }
  console.error('[viashift] scan_decrement insert failed:', error);
  return { kind: 'error', message: error.message };
}

/**
 * Compensating event for an undone scan. Written as a
 * manual_adjustment so the audit trail captures both the
 * original scan and the intentional reversal — never via
 * a "delete the event" path, which would lose history.
 */
export async function recordScanUndo(
  clinicId: string,
  productId: string,
  userId: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  const { error } = await supabase.from('inventory_events').insert({
    clinic_id: clinicId,
    product_id: productId,
    kind: 'manual_adjustment',
    delta: 1,
    count_after: 0,
    actor_id: userId,
    notes: 'scan-undo',
  });
  if (error) {
    console.error('[viashift] scan-undo insert failed:', error);
    return { ok: false, message: error.message };
  }
  return { ok: true };
}

/**
 * Assign a barcode to a product via the SECURITY DEFINER RPC.
 * Returns the structured `result_code` from the function for
 * 'ok' / 'conflict'; harder failures (forbidden, not_found,
 * invalid_barcode) come back as `error` because the UI
 * shouldn't be reaching them under normal flow.
 */
export type AssignBarcodeResult =
  | { kind: 'ok' }
  | { kind: 'conflict' }
  | { kind: 'error'; message: string };

export async function assignBarcodeToProduct(
  clinicId: string,
  productId: string,
  barcode: string,
): Promise<AssignBarcodeResult> {
  const { data, error } = await supabase.rpc('set_product_barcode', {
    p_clinic_id: clinicId,
    p_product_id: productId,
    p_barcode: barcode,
  });
  if (error) {
    console.error('[viashift] set_product_barcode failed:', error);
    return { kind: 'error', message: error.message };
  }
  if (data === 'ok') return { kind: 'ok' };
  if (data === 'conflict') return { kind: 'conflict' };
  // Defensive: function should only return 'ok' | 'conflict'.
  // Anything else means the schema and the client diverged.
  console.error('[viashift] Unexpected RPC result:', data);
  return { kind: 'error', message: `Unexpected result: ${String(data)}` };
}
